// Create a roster for each team.
// Even while not logged in, you can see a list of players within the chosen team.
// This page outputs that list.

import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import './Players.css'
import '../../shared/schedule/FullScheduleCards.css'

import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import Footer from '../../shared/components/UIElements/Footer'
import StatsAndStandingsHome from '../../shared/components/Navigation/StatsAndStandingsHome'

const RosterAndSchedulePage = () => {
	console.log('inside RosterAndSchedulePage.js')

	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)

	const [rosteredPlayers, setRosteredPlayers] = useState()
	const [gamesList, setGamesList] = useState()

	const [wins, setWins] = useState(0)
	const [losses, setLosses] = useState(0)
	const [overtimeLosses, setOvertimeLosses] = useState(0)
	const [shootoutLosses, setShootoutLosses] = useState(0)
	const [ties, setTies] = useState(0)

	const [leagueName, setLeagueName] = useState()
	const [teamId, setTeamId] = useState()
	const [photo, setPhoto] = useState()

	const { leagueId, session, year, teamName } = useParams()

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/${session}/${year}/${teamName}`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(
						responseData.message || 'Unable to load the team roster.',
					)
				}

				setRosteredPlayers(responseData.rosteredPlayers)
				setWins(responseData.wins || 0)
				setLosses(responseData.losses || 0)
				setOvertimeLosses(responseData.overtimeLosses || 0)
				setShootoutLosses(responseData.shootoutLosses || 0)
				setTies(responseData.ties || 0)
				setLeagueName(responseData.leagueName)
				setPhoto(responseData.photo)
			} catch (err) {
				setError(err.message)
			}

			setIsLoading(false)
		}

		sendRequest()
	}, [leagueId, session, year, teamName])

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/schedule/${leagueId}/${session}/${year}/${teamName}`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(
						responseData.message || 'Unable to load the team schedule.',
					)
				}

				setGamesList(responseData.allGamesArray)
				setTeamId(responseData.teamId)
			} catch (err) {
				setError(err.message)
			}

			setIsLoading(false)
		}

		sendRequest()
	}, [leagueId, session, year, teamName])

	const errorHandler = () => {
		setError(null)
	}

	const formatGameDate = (game) => {
		if (!game?.date) {
			return ''
		}

		const months = [
			'',
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'June',
			'July',
			'Aug',
			'Sept',
			'Oct',
			'Nov',
			'Dec',
		]

		const dateParts = game.date.split(/[-/]/)
		const month = Number(dateParts[0])
		const day = Number(dateParts[1])

		if (!months[month] || !day) {
			return `${game.dayOfWeek || ''} ${game.date}`.trim()
		}

		return `${game.dayOfWeek || ''}, ${months[month]} ${day}`.replace(
			/^,\s*/,
			'',
		)
	}

	const formatGameTime = (time) => {
		if (!time) {
			return ''
		}

		const [hourValue, minuteValue = '00'] = time.split(':')
		let hour = Number(hourValue)

		if (Number.isNaN(hour)) {
			return time
		}

		const period = hour >= 12 ? 'pm' : 'am'

		hour = hour % 12 || 12

		return `${hour}:${minuteValue} ${period}`
	}

	const isTeamWinner = (game, displayedTeamName) => {
		if (!game?.winner || !teamId) {
			return false
		}

		if (displayedTeamName === teamName) {
			return game.winner === teamId
		}

		return game.winner !== teamId
	}

	const getDisplayScore = (game) => {
		if (!game?.score) {
			return 'TBP'
		}

		const scoreParts = String(game.score)
			.split(/\s*-\s*/)
			.map((score) => score.trim())

		if (scoreParts.length !== 2) {
			return game.score
		}

		const firstScore = Number(scoreParts[0])
		const secondScore = Number(scoreParts[1])

		if (Number.isNaN(firstScore) || Number.isNaN(secondScore)) {
			return game.score
		}

		const highScore = Math.max(firstScore, secondScore)
		const lowScore = Math.min(firstScore, secondScore)

		const homeWon = isTeamWinner(game, game.homeTeamName)
		const visitorWon = isTeamWinner(game, game.visitorTeamName)

		/*
		Card order is always:

		HOME TEAM     HOME SCORE - VISITOR SCORE     VISITOR TEAM
	*/

		if (homeWon) {
			return `${highScore} - ${lowScore}`
		}

		if (visitorWon) {
			return `${lowScore} - ${highScore}`
		}

		// Tied games
		return `${firstScore} - ${secondScore}`
	}

	const getTeamLink = (displayedTeamName) => {
		if (!displayedTeamName || displayedTeamName === teamName) {
			return null
		}

		return `/league/${leagueId}/${session}/${year}/${displayedTeamName}`
	}

	const getGameStatus = (game) => {
		if (!game.score) {
			return 'Scheduled'
		}

		if (!game.winner && !game.loser) {
			return 'Tied'
		}

		return 'Final'
	}

	const getGameResultSuffix = (game) => {
		if (game?.status === 'Overtime') {
			return 'OT'
		}

		if (game?.status === 'Shootout') {
			return 'SO'
		}

		return ''
	}

	const renderTeamName = (displayedTeamName, winner) => {
		const teamLink = getTeamLink(displayedTeamName)

		const teamNameContent = (
			<span className={winner ? 'scheduleCard__winnerPill' : ''}>
				{displayedTeamName}
			</span>
		)

		if (!teamLink) {
			return teamNameContent
		}

		return (
			<Link
				to={teamLink}
				style={{
					color: 'inherit',
					textDecoration: 'none',
				}}
			>
				{teamNameContent}
			</Link>
		)
	}

	return (
		<div style={{ overflowX: 'hidden' }}>
			<StatsAndStandingsHome />

			<ErrorModal error={error} onClear={errorHandler} />

			{isLoading && (
				<div className='center'>
					<LoadingSpinner />
				</div>
			)}

			{!isLoading && rosteredPlayers && gamesList && (
				<React.Fragment>
					<div className='Roster teamRosterSection'>
						{/* Existing desktop/tablet header */}
						<div className='teamRosterDesktopHeader'>
							<h1>{teamName}</h1>

							<h3>{leagueName}</h3>

							<h3>
								{session} {year}
							</h3>

							{photo && (
								<div className='photoDiv'>
									<img
										src={`${process.env.REACT_APP_AWS_BUCKET}/${photo}`}
										className='teamPhoto'
										alt={`${teamName} logo`}
									/>
								</div>
							)}

							<h3 className='h3Center'>
								Record: {wins} - {losses} - {ties} -{' '}
								{overtimeLosses + shootoutLosses}
							</h3>
						</div>

						{/* New mobile-only team card */}
						<div className='teamRosterMobileHero'>
							{photo && (
								<img
									src={`${process.env.REACT_APP_AWS_BUCKET}/${photo}`}
									className='teamRosterMobileHero__logo'
									alt={`${teamName} logo`}
								/>
							)}

							<h1 className='teamRosterMobileHero__teamName'>{teamName}</h1>

							<h2 className='teamRosterMobileHero__league'>{leagueName}</h2>

							<div className='teamRosterMobileHero__season'>
								{session} {year}
							</div>

							<div className='teamRosterMobileHero__divider'></div>

							<div className='teamRosterMobileHero__recordTitle'>
								Team Record
							</div>

							<div className='teamRosterMobileHero__record'>
								<div className='teamRosterMobileHero__recordItem'>
									<strong>{wins}</strong>
									<span>Wins</span>
								</div>

								<div className='teamRosterMobileHero__recordItem'>
									<strong>{losses}</strong>
									<span>Losses</span>
								</div>

								<div className='teamRosterMobileHero__recordItem'>
									<strong>{ties}</strong>
									<span>Ties</span>
								</div>

								<div className='teamRosterMobileHero__recordItem'>
									<strong>{overtimeLosses + shootoutLosses}</strong>
									<span>OTL/SOL</span>
								</div>
							</div>
						</div>

						<p className='clickOnThe teamRosterInstructions'>
							Click on a player name to see their stat history
						</p>

						<div className='teamRosterTableCard'>
							<table id='roster' className='teamRosterTable'>
								<thead>
									<tr>
										<th>#</th>
										<th>Name</th>
										<th>G</th>
										<th>A</th>
										<th>Points</th>
										<th>PIM</th>
									</tr>
								</thead>

								<tbody>
									{rosteredPlayers.map((player) => (
										<tr key={player.playerId || player._id}>
											<td className='teamRosterTable__number'>
												{player.number}
											</td>

											<td className='teamRosterTable__name'>
												<Link
													className='teamRosterTable__playerLink'
													to={`/${player.playerId}/history`}
												>
													<span>
														{player.firstName} {player.lastName}
													</span>

													{(player.isCaptain || player.isGoalie) && (
														<small className='teamRosterTable__playerRole'>
															{player.isCaptain && '(Captain)'}
															{player.isCaptain && player.isGoalie && ' '}
															{player.isGoalie && '(Goalie)'}
														</small>
													)}
												</Link>
											</td>

											<td>{player.goals}</td>
											<td>{player.assists}</td>

											<td className='teamRosterTable__points'>
												{player.goals + player.assists}
											</td>

											<td>{player.pim}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					<div className='Roster teamScheduleSection'>
						<h1>Schedule:</h1>

						<p className='clickOnThe teamScheduleInstructions'>
							(Click on the final score of a game to see a Game Summary)
						</p>

						<p className='clickOnThe teamScheduleInstructions'>
							(Click on an opposing team to go to their roster and schedule
							page)
						</p>

						{/* Desktop and tablet schedule */}
						<table id='teamSchedule' className='teamScheduleDesktopTable'>
							<thead>
								<tr>
									<th>Date/Time/Location</th>
									<th>Opponent</th>
									<th>Score</th>
								</tr>
							</thead>

							<tbody>
								{gamesList.map((game, key) => {
									const isHomeTeam = game.homeTeamName === teamName

									const opponentName = isHomeTeam
										? game.visitorTeamName
										: game.homeTeamName

									const opponentLink = getTeamLink(opponentName)

									return (
										<tr key={game._id || key}>
											<td>
												{formatGameDate(game)}
												{', '}
												{formatGameTime(game.time)}
												<br />
												{' @ '}
												<br />
												{game.venueName}
											</td>

											<td>
												{opponentLink ? (
													<Link
														style={{
															textDecoration: 'none',
															color: 'black',
															fontWeight: 600,
														}}
														to={opponentLink}
													>
														{opponentName}
													</Link>
												) : (
													<span>{opponentName}</span>
												)}

												{game.description && (
													<div
														style={{
															color: 'red',
															fontWeight: 'bold',
														}}
													>
														({game.description})
													</div>
												)}
											</td>

											{!game.score && <td></td>}

											{game.score && !game.winner && !game.loser && (
												<td
													style={{
														color: 'black',
														fontWeight: 'bold',
													}}
												>
													<Link
														style={{
															textDecoration: 'none',
															fontWeight: 600,
															color: 'black',
														}}
														to={`/gameSummary/${game._id}`}
													>
														TIED {game.score}
													</Link>
												</td>
											)}

											{game.loser === teamId && (
												<td
													style={{
														color: 'red',
														fontWeight: 'bold',
													}}
												>
													<Link
														style={{
															textDecoration: 'none',
															fontWeight: 600,
															color: 'red',
														}}
														to={`/gameSummary/${game._id}`}
													>
														LOST {game.score} {getGameResultSuffix(game)}
													</Link>
												</td>
											)}

											{game.winner === teamId && (
												<td
													style={{
														color: 'green',
														fontWeight: 'bold',
													}}
												>
													<Link
														style={{
															textDecoration: 'none',
															fontWeight: 600,
															color: 'green',
														}}
														to={`/gameSummary/${game._id}`}
													>
														WON {game.score} {getGameResultSuffix(game)}
													</Link>
												</td>
											)}
										</tr>
									)
								})}
							</tbody>
						</table>

						{/* Mobile schedule cards */}
						<div className='scheduleCardsMobile'>
							{gamesList.map((game, key) => {
								const homeWon = isTeamWinner(game, game.homeTeamName)
								const visitorWon = isTeamWinner(game, game.visitorTeamName)

								const homeLink = getTeamLink(game.homeTeamName)
								const visitorLink = getTeamLink(game.visitorTeamName)

								return (
									<div
										className={`scheduleCard ${
											game.description ? 'scheduleCardSpecial' : ''
										}`}
										key={game._id || key}
									>
										<div className='scheduleCard__header'>
											<div>{formatGameDate(game)}</div>

											<div className='scheduleCard__league'>{leagueName}</div>

											<div>{formatGameTime(game.time)}</div>
										</div>

										<div className='scheduleCard__body'>
											{/* Home team first */}
											<div className='scheduleCard__team'>
												{homeLink ? (
													<Link
														to={homeLink}
														className={`scheduleCard__teamLink ${
															homeWon ? 'scheduleCard__winnerPill' : ''
														}`}
													>
														{game.homeTeamName}
													</Link>
												) : (
													<span
														className={
															homeWon ? 'scheduleCard__winnerPill' : ''
														}
													>
														{game.homeTeamName}
													</span>
												)}
											</div>

											<div className='scheduleCard__scoreBox'>
												{game.score ? (
													<>
														<Link
															to={`/gameSummary/${game._id}`}
															className='scheduleCard__score'
														>
															{getDisplayScore(game)}
														</Link>

														{getGameResultSuffix(game) && (
															<div className='scheduleCard__resultType'>
																{getGameResultSuffix(game)}
															</div>
														)}
													</>
												) : (
													<div className='scheduleCard__score'>TBP</div>
												)}
											</div>

											{/* Visitor team second */}
											<div className='scheduleCard__team'>
												{visitorLink ? (
													<Link
														to={visitorLink}
														className={`scheduleCard__teamLink ${
															visitorWon ? 'scheduleCard__winnerPill' : ''
														}`}
													>
														{game.visitorTeamName}
													</Link>
												) : (
													<span
														className={
															visitorWon ? 'scheduleCard__winnerPill' : ''
														}
													>
														{game.visitorTeamName}
													</span>
												)}
											</div>
										</div>

										{game.venueName && (
											<div className='scheduleCard__venue'>
												📍 {game.venueName}
											</div>
										)}

										{game.description && (
											<div className='scheduleCard__description'>
												{game.description}
											</div>
										)}
									</div>
								)
							})}
						</div>
					</div>

					<Footer />
				</React.Fragment>
			)}
		</div>
	)
}

export default RosterAndSchedulePage
